<template>
  <div>
    <!-- Guest -->
    <button v-if="!isLoggedIn" class="btn btn-sign-in" @click="$refs.modal.show()">Sign In</button>

    <!-- Member -->
    <div v-else class="profile-wrap">
      <button class="btn btn-profile" @click="profileOpen = !profileOpen">
        <span class="profile-icon">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M12 12c2.7 0 4.8-2.1 4.8-4.8S14.7 2.4 12 2.4 7.2 4.5 7.2 7.2 9.3 12 12 12zm0 2.4c-3.2 0-9.6 1.6-9.6 4.8v2.4h19.2v-2.4c0-3.2-6.4-4.8-9.6-4.8z"/></svg>
        </span><span class="profile-email"> {{ userEmail }}</span>
      </button>
      <div v-if="profileOpen" class="profile-dropdown">
        <a class="dropdown-item" href="/profile">Profile</a>
        <a class="dropdown-item" href="/favorites">My Favorites</a>
        <button class="dropdown-item" @click="logout">Log Out</button>
      </div>
    </div>

    <app-modal ref="modal" :hide-header="false" :title="mode === 'login' ? 'Sign In' : 'Create Account'" size="sm" centered @close="reset">
      <div class="auth-content">
        <div class="auth-tabs">
          <button :class="['auth-tab', { active: mode === 'login' }]" @click="mode = 'login'">Sign In</button>
          <button :class="['auth-tab', { active: mode === 'signup' }]" @click="mode = 'signup'">Sign Up</button>
        </div>

        <!-- Sign In -->
        <form v-if="mode === 'login'" @submit.prevent="submitLogin">
          <div class="auth-field">
            <label>Email</label>
            <input v-model="email" type="email" required autocomplete="email" />
          </div>
          <div class="auth-field">
            <label>Password</label>
            <input v-model="password" type="password" required autocomplete="current-password" />
          </div>
          <p v-if="error" class="auth-error">{{ error }}</p>
          <button type="submit" class="btn btn-auth-submit" :disabled="loading">
            {{ loading ? 'Signing in…' : 'Sign In' }}
          </button>
          <p class="auth-switch">No account? <a href="#" @click.prevent="mode = 'signup'">Sign Up</a></p>
        </form>

        <!-- Sign Up -->
        <form v-if="mode === 'signup'" @submit.prevent="submitSignup">
          <div class="auth-field">
            <label>Display Name</label>
            <input v-model="displayName" type="text" required autocomplete="username" minlength="3" maxlength="20" pattern="[a-zA-Z0-9_]+" title="3–20 characters: letters, numbers, and underscores only" />
          </div>
          <div class="auth-field">
            <label>Email</label>
            <input v-model="email" type="email" required autocomplete="email" />
          </div>
          <div class="auth-field">
            <label>Password</label>
            <input v-model="password" type="password" required autocomplete="new-password" minlength="6" />
          </div>
          <div class="auth-check">
            <input id="chk-terms" v-model="acceptedTerms" type="checkbox" required />
            <label for="chk-terms">I agree to the <a href="/privacy" target="_blank">Terms &amp; Privacy Policy</a></label>
          </div>
          <div class="auth-check">
            <input id="chk-newsletter" v-model="newsletter" type="checkbox" />
            <label for="chk-newsletter">Send me updates and game recommendations</label>
          </div>
          <p v-if="error" class="auth-error">{{ error }}</p>
          <button type="submit" class="btn btn-auth-submit" :disabled="loading || !acceptedTerms">
            {{ loading ? 'Creating account…' : 'Create Account' }}
          </button>
          <p class="auth-switch">Already have an account? <a href="#" @click.prevent="mode = 'login'">Sign In</a></p>
        </form>
      </div>
    </app-modal>
  </div>
</template>

<script>
import axios from 'axios';
import AppModal from './AppModal.vue';

export default {
  name: 'AuthModal',
  components: { AppModal },
  mounted() {
    this._onShowAuth = () => this.$refs.modal.show();
    window.addEventListener('gmg:show-auth', this._onShowAuth);
  },
  beforeUnmount() {
    window.removeEventListener('gmg:show-auth', this._onShowAuth);
  },
  data() {
    return {
      isLoggedIn: window.__gmgUser?.type === 'MEMBER',
      userEmail: window.__gmgUser?.email ?? '',
      profileOpen: false,
      mode: 'login',
      email: '',
      password: '',
      displayName: '',
      acceptedTerms: false,
      newsletter: false,
      error: null,
      loading: false,
    };
  },
  methods: {
    async logout() {
      await axios.post('/auth/logout');
      window.location.reload();
    },
    reset() {
      this.error = null;
      this.email = '';
      this.password = '';
      this.displayName = '';
      this.acceptedTerms = false;
      this.newsletter = false;
    },
    async submitLogin() {
      this.error = null;
      this.loading = true;
      try {
        await axios.post('/auth/login', { email: this.email, password: this.password });
        this.$refs.modal.hide();
        window.location.reload();
      } catch (e) {
        this.error = e.response?.data?.message || 'Login failed. Please try again.';
      } finally {
        this.loading = false;
      }
    },
    async submitSignup() {
      this.error = null;
      this.loading = true;
      try {
        await axios.post('/auth/signup', { email: this.email, password: this.password, display_name: this.displayName, newsletter_subscribed: this.newsletter });
        this.$refs.modal.hide();
        window.location.reload();
      } catch (e) {
        const errors = e.response?.data?.errors;
        this.error = errors ? Object.values(errors).flat()[0] : (e.response?.data?.message || 'Sign up failed. Please try again.');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.btn-sign-in {
  background: linear-gradient(135deg, var(--accent-blue), var(--accent-purple));
  color: #fff;
  border: none;
  padding: 6px 18px;
  border-radius: 20px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  white-space: nowrap;
  transition: opacity 0.2s;
}
.btn-sign-in:hover { opacity: 0.85; }

.profile-wrap {
  position: relative;
}
.btn-profile {
  background: var(--card-bg);
  color: var(--text-primary);
  border: 1px solid var(--border-color);
  padding: 6px 14px;
  border-radius: 20px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
  transition: border-color 0.2s;
}
.btn-profile:hover { border-color: var(--accent-blue); }

@media (max-width: 1399px) {
  .profile-email { display: none; }
  .btn-profile { padding: 6px 10px; }
  .btn-sign-in { font-size: 0.8rem; padding: 5px 12px; }
}

.profile-dropdown {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  background: var(--card-bg);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  min-width: 120px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.4);
  z-index: 999;
  overflow: hidden;
}
.dropdown-item {
  display: block;
  width: 100%;
  background: none;
  border: none;
  padding: 10px 16px;
  text-align: left;
  font-size: 0.9rem;
  color: var(--text-primary);
  cursor: pointer;
}
.dropdown-item:hover { background: var(--secondary-bg); }

.auth-content {
  width: 300px;
  padding: 8px 4px 4px;
}

.auth-tabs {
  display: flex;
  gap: 8px;
  margin-bottom: 20px;
}
.auth-tab {
  flex: 1;
  background: none;
  border: 1px solid #ddd;
  color: #888;
  border-radius: 8px;
  padding: 8px;
  font-size: 0.95rem;
  cursor: pointer;
  transition: all 0.2s;
}
.auth-tab.active {
  background: linear-gradient(135deg, var(--accent-blue), var(--accent-purple));
  border-color: transparent;
  color: #fff;
  font-weight: 600;
}

.auth-field {
  margin-bottom: 14px;
}
.auth-field label {
  display: block;
  font-size: 0.85rem;
  color: #555;
  margin-bottom: 5px;
}
.auth-field input {
  width: 100%;
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 9px 12px;
  font-size: 0.95rem;
  box-sizing: border-box;
  transition: border-color 0.2s;
  color: #333;
}
.auth-field input:focus {
  outline: none;
  border-color: var(--accent-blue);
}

.auth-check {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
  font-size: 0.85rem;
  color: #555;
}
.auth-check input[type="checkbox"] {
  margin-top: 2px;
  flex-shrink: 0;
  cursor: pointer;
}
.auth-check label {
  cursor: pointer;
  line-height: 1.4;
}

.auth-error {
  color: #e17055;
  font-size: 0.85rem;
  margin-bottom: 10px;
}

.btn-auth-submit {
  width: 100%;
  background: linear-gradient(135deg, var(--accent-blue), var(--accent-purple));
  color: #fff;
  border: none;
  border-radius: 8px;
  padding: 10px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: opacity 0.2s;
}
.btn-auth-submit:disabled { opacity: 0.6; cursor: not-allowed; }
.btn-auth-submit:not(:disabled):hover { opacity: 0.85; }

.auth-switch {
  text-align: center;
  font-size: 0.85rem;
  color: #888;
  margin-top: 14px;
  margin-bottom: 0;
}
</style>
